import { useEffect, useRef } from "react";
import { InteractionStatus } from "@azure/msal-browser";
import {
  MAX_SESSION_MS,
  SESSION_LOGOUT_REASON_ABSOLUTE,
} from "../utils/session.constants";
import { sessionLogout } from "../utils/sessionLogout";
import { msalReady } from "../utils/msalInstance";

const SESSION_START_KEY = "ctpo-session-start";

function absoluteDeadlineMs(acc) {
  const accountId = acc?.localAccountId;
  const key = `${SESSION_START_KEY}:${accountId}`;
  try {
    const stored = sessionStorage.getItem(key);
    if (stored != null) return Number(stored) + MAX_SESSION_MS;
  } catch { /* ignore */ }

  const startMs = Date.now();
  try { sessionStorage.setItem(key, String(startMs)); } catch { /* ignore */ }
  return startMs + MAX_SESSION_MS;
}

export function useAbsoluteSessionTimeout({
  instance,
  account,
  isAuthenticated,
  inProgress,
}) {
  const timerRef = useRef(null);
  const instanceRef = useRef(instance);
  const accountRef = useRef(account);
  instanceRef.current = instance;
  accountRef.current = account;

  useEffect(() => {
    let cancelled = false;

    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (
      !isAuthenticated ||
      !account ||
      inProgress !== InteractionStatus.None
    ) {
      return undefined;
    }

    const clearTimer = () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleLogout = (inst, acc, delayMs) => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        sessionLogout(inst, acc, {
          reason: SESSION_LOGOUT_REASON_ABSOLUTE,
          broadcast: true,
        });
      }, delayMs);
    };

    const armAfterMsalReady = () => {
      if (cancelled) return;

      const inst = instanceRef.current;
      const acc = inst.getActiveAccount() || accountRef.current;
      if (!acc) return;

      const deadlineMs = absoluteDeadlineMs(acc);
      const delay = deadlineMs - Date.now();

      if (delay <= 0) {
        sessionLogout(inst, acc, {
          reason: SESSION_LOGOUT_REASON_ABSOLUTE,
          broadcast: true,
        });
      } else {
        scheduleLogout(inst, acc, delay);
      }
    };

    msalReady.then(() => {
      if (!cancelled) armAfterMsalReady();
    });

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [instance, account, isAuthenticated, inProgress]);
}
